import { DdEntryMerged, DdContextEntriesMap, DataDictionary } from './../../model/data-dictionary';
import { Injectable, inject, signal } from '@angular/core';
import { GlobalProperties } from '../../model/global-properties';
import { SystemProperties } from '../../model/system-properties';
import { HisAuthService } from '../../../auth/service/his-auth.service';
import { HisSessionStorageService } from './his-session-storage.service';
import { NavigationItem } from '../../model/navigation-item';
import { HisDdService } from './his-dd.service';

/**
 * provides application wide global variables used by the infrastructure.
 */
@Injectable({providedIn: 'root'})
export class HisBaseGlobalsService {
    private auth = inject(HisAuthService);
    private sessionStorageService = inject(HisSessionStorageService);
    private ddService = inject(HisDdService);

    private ui: Record<string, string> = {};
    private lastId = 1;
    public lang: string;
    public translationLanguages: string;
    private currentDevice: string;
    private modules: Set<string>;
    public system = new SystemProperties();
    public dataDictionaryEditMode = signal(false);
    public conf: Record<string, any> = {};

    // TODO hier lieber gleich nur die Children-List speichern? Dann bräuchten wir keine Dummy-Daten!
    private navigation: NavigationItem = {
        name: 'dummy name',
        link: 'dummy link',
        linkType: 'dummy linktype',
        target: 'dummy target',
        text: 'dummy text',
        title: 'dummy title',
        iconName: 'dummy iconName',
        iconWidth: 'dummy icon width',
        cssclass: 'dummy css class',
        description: 'dummy description',
        shortcut: 'dummy shortcut',
        children: []
    };

    // TODO hier lieber gleich nur die Children-List speichern? Dann bräuchten wir keine Dummy-Daten!
    private bookmarks: NavigationItem = {
        name: 'dummy name',
        link: 'dummy link',
        linkType: 'dummy linktype',
        target: 'dummy target',
        text: 'dummy text',
        title: 'dummy title',
        iconName: 'dummy iconName',
        iconWidth: 'dummy icon width',
        cssclass: 'dummy css class',
        description: 'dummy description',
        shortcut: 'dummy shortcut',
        children: []
    };
    htmlEditorEnter: string;
    htmlEditorShiftEnter: string;
    summernoteColors: string;
    versionHash: string;

    private static pad0( n: number ): string {
        let text = '';
        for ( let i = 0; i < n; i++ ) {
            text = text + '0';
        }
        return text;
    }

    setLanguage( lang: string ) {
        console.log( 'HisBaseGlobalsService: set lang = ' + lang );
        this.lang = lang;
    }

    getLanguage(): string {
        return this.lang;
    }

    /**
     * Da Angular neben 'en-US' nur die in der 'his-core-module.ts' explizit importierten Locales unterstützt,
     * passiert hier ein Mapping der HISinOne-Locales auf diese Locales.
     * Das kann / muss bei Bedarf erweitert werden
     */
    getNearestLocaleSupportedByClient(): string {
        if ( this.lang === 'de' || this.lang.startsWith( 'de-' ) ) {
            return 'de';
        } else {
            return 'en';
        }
    }

    /**
     * @return the languages supported by the server
     */
    getSupportedLanguages(): string {
        return this.system.languages;
    }
    /**
     * @return wmenu "megaMenu" is to be displayed
     */
    getMenuType(): string {
        return this.system.menuType;
    }

    getCurrentDevice(): string {
        return this.currentDevice;
    }

    getNavigation(): NavigationItem {
        return this.navigation;
    }

    getBookmarks(): NavigationItem {
        return this.bookmarks;
    }

    setUi( ui: Record<string, string> ) {
        console.log( 'HisBaseGlobalsService: set ui' );
        this.ui = ui;
    }

    getUi( key: string ) {
        let label = this.ui[key] ?? null; // lieber null als undefined, falls nciht vorhanden
        return label;
    }

    setDd( dd: DataDictionary ) {
        this.ddService.setDd( dd );
    }

    setDdEntry(key: string, entry: DdEntryMerged, context?: string) {
        this.ddService.setDdEntry( key, entry, context );
    }

    setRequiredToDdEntry( key: string, value: boolean ) {
        this.ddService.setRequiredToDdEntry( key, value );
    }

    /**
     * get data dictionary entry for given key and an optional list of contexts. The last context has the highest priority.
     */
    getDd( key: string, contexts?: string[] ) {
        return this.ddService.getMergedEntry( key, contexts );
    }

    /**
     * Returns the (unmerged) DD entry for exactly the given context, without merging it with 'DEFAULT'.
     * Used to determine whether an explicit configuration exists for a specific context.
     */
    getDdForContext( key: string, context?: string ) {
        return this.ddService.getEntryForContext( key, context );
    }

    setGlobalProperties( globalProperties: GlobalProperties ) {
        // console.log( 'global properties', globalProperties );
        this.lang = globalProperties.user.language;
        this.translationLanguages = globalProperties.translationLanguages;
        this.currentDevice = globalProperties.user.currentDevice;
        this.auth.init( globalProperties.user );
        this.sessionStorageService.clearOnUserChange( this.auth );
        this.conf = globalProperties.conf;
        this.system = globalProperties.system;
        this.modules = new Set<string>( globalProperties.system.modules.split( /\s*,\s*/ ) );
        console.log( 'set global properties: navigation', globalProperties.navigation );
        this.navigation = globalProperties.navigation;
        this.bookmarks = globalProperties.user.bookmarks;
        this.htmlEditorEnter = this.system.htmlEditorEnter || '2'; // default 2 = erzeugt <br> | 1 erzeugt <p></p>
        this.htmlEditorShiftEnter = this.system.htmlEditorShiftEnter || '1';
        this.versionHash = this.system.versionHash;
        console.log( 'the bookmarks', this.bookmarks );
    }

    getImage( name: string ) {
        if ( !name ) {
            return null;
        }
        return this.system.images['IMG_' + name.toUpperCase()];
    }

    isAnyModuleActive( ...modules: string[] ) {
        for ( let module2 of modules ) {
            if ( this.modules.has( module2 ) ) {
                return true;
            }
        }
        return false;
    }

    /**
     * returns a counter usable as suffix in html id attributes
     */
    nextId(): number {
        this.lastId++;
        return this.lastId;
    }

    /**
     * Creates a new unique ID using the current date/time and a sequence number.
     * The created ID is guaranteed to be unique only for the current browser session.
     * If no prefix and no postfix are defined, the length of the created ID is 16.
     *
     * @param prefix The prefix of the ID (may be undefined)
     * @param postfix The postfix of the ID (may be undefined)
     */
    uid( prefix?: string, postfix?: string ): string {
        let radix = 36;
        let now = new Date().getTime().toString( radix );

        let counter = this.nextId().toString( radix );

        let id = now;

        let n = 16 - now.length - counter.length;
        if ( n > 0 ) { id = id + HisBaseGlobalsService.pad0( n ); }

        id = id + counter;

        if ( prefix ) { id = prefix + id; }
        if ( postfix ) { id = id + postfix; }

        return id;
    }
}
